/**
 * Accesos de clientes a proyectos (invitaciones).
 *
 * - Invitar / reenviar: Edge Function "invitar-cliente-proyecto" (la clave
 *   service_role vive solo en el servidor; aquí viaja el JWT del admin).
 * - Listar / revocar: RPC protegidas por es_admin().
 * - Aceptar: RPC aceptar_invitacion con la sesión del cliente; la base exige
 *   que el correo confirmado de la sesión coincida con el invitado.
 */
import { supabase, SUPABASE_URL } from "./supabase";

const FUNCION = "invitar-cliente-proyecto";
const URL_FUNCION = `${SUPABASE_URL}/functions/v1/${FUNCION}`;
const CLAVE_TOKEN = "tuaplicacion_invitacion";

// ── Errores con código: el panel decide qué mostrar y si conviene reintentar ─
// global = afecta a todas las invitaciones (no tiene sentido seguir con las demás).
export const ERRORES_ACCESO = {
  no_publicada: { global: true,
    texto: "El servicio de invitaciones todavía no está publicado en el servidor. No se envió ninguna invitación." },
  no_instalado: { global: true,
    texto: "El módulo de invitaciones aún no está instalado en la base de datos. No se envió ninguna invitación." },
  correo_no_configurado: { global: true,
    texto: "El envío de correos no está configurado en el servidor. No se creó ninguna invitación." },
  configuracion: { global: true,
    texto: "El servicio de invitaciones no está configurado en el servidor. No se envió ninguna invitación." },
  sin_conexion: { global: true,
    texto: "No hay conexión con el servidor. Revisa tu internet e intenta de nuevo. No se envió la invitación." },
  origen_no_autorizado: { global: true,
    texto: "El servicio de invitaciones no acepta solicitudes desde esta dirección del panel. No se envió ninguna invitación." },
  servicio_no_disponible: { global: true,
    texto: "El servicio de invitaciones no respondió. Intenta nuevamente en unos minutos. No se envió la invitación." },
  sesion:   { global: true, texto: "Tu sesión expiró. Vuelve a iniciar sesión e intenta de nuevo." },
  permisos: { global: true, texto: "No tienes permisos para invitar clientes." },
  correo_fallido: { global: false,
    texto: "La invitación quedó registrada, pero el correo no pudo enviarse. Usa “Reenviar” en Accesos." },
  servidor: { global: false, texto: "El servidor no pudo procesar la invitación. Intenta de nuevo." },
};

export class ErrorAccesos extends Error {
  constructor(codigo, texto) {
    super(texto || ERRORES_ACCESO[codigo]?.texto || ERRORES_ACCESO.servidor.texto);
    this.name = "ErrorAccesos";
    this.codigo = codigo;
    this.global = !!ERRORES_ACCESO[codigo]?.global;
  }
}

const CODIGO_SERVIDOR = {           // códigos que devuelve la Edge Function
  modulo_no_instalado: "no_instalado", correo_no_configurado: "correo_no_configurado",
  configuracion: "configuracion", sesion: "sesion", permisos: "permisos", correo_fallido: "correo_fallido",
};

// La petición no llegó a leerse (fetch falló). Con una petición simple, sin
// encabezados ni credenciales (no requiere preflight), se distingue:
//   404 + sb-error-code NOT_FOUND → la función no está publicada
//   cualquier otra respuesta     → existe, pero el navegador bloqueó la llamada (origen)
//   falla de red                 → sin conexión
async function diagnosticarFallaDeEnvio() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "sin_conexion";
  try {
    const r = await fetch(URL_FUNCION, { method: "GET", cache: "no-store", credentials: "omit" });
    if (r.status === 404 && r.headers.get("sb-error-code") === "NOT_FOUND") return "no_publicada";
    return "origen_no_autorizado";
  } catch {
    return "sin_conexion";
  }
}

async function errorDeFuncion(error) {
  if (error?.name === "FunctionsFetchError") return new ErrorAccesos(await diagnosticarFallaDeEnvio());
  if (error?.name === "FunctionsRelayError") return new ErrorAccesos("servicio_no_disponible");
  const resp = error?.context;
  if (error?.name === "FunctionsHttpError" && resp) {
    if (resp.status === 404 && resp.headers?.get?.("sb-error-code") === "NOT_FOUND") return new ErrorAccesos("no_publicada");
    let cuerpo = null;
    try { cuerpo = await resp.json(); } catch { /* sin cuerpo JSON */ }
    const codigo = CODIGO_SERVIDOR[cuerpo?.codigo]
      || (resp.status === 401 ? "sesion" : resp.status === 403 ? "permisos"
        : resp.status === 503 || resp.status === 504 ? "servicio_no_disponible" : "servidor");
    // Mensajes de la función: ya están redactados para el usuario.
    return new ErrorAccesos(codigo, cuerpo?.error);
  }
  if (/Failed to fetch|NetworkError|Load failed/i.test(error?.message || "")) return new ErrorAccesos("sin_conexion");
  return new ErrorAccesos("servidor");
}

// RPC del módulo: si la base no las tiene, el módulo no está instalado.
function errorDeRpc(error) {
  const codigo = String(error?.code ?? "");
  if (["PGRST202", "PGRST205", "42883", "42P01"].includes(codigo)
      || /Could not find the (function|table)/i.test(error?.message || "")) {
    return new ErrorAccesos("no_instalado",
      "El módulo de accesos de clientes aún no está instalado en la base de datos.");
  }
  if (/Failed to fetch|NetworkError|Load failed/i.test(error?.message || "")) return new ErrorAccesos("sin_conexion",
    "No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.");
  return error;
}

export const ESTADOS_ACCESO = {
  pendiente: { label: "Pendiente", color: "#f59e0b", bg: "#fef3c7" },
  aceptada:  { label: "Activo",    color: "#10b981", bg: "#d1fae5" },
  vencida:   { label: "Vencida",   color: "#f97316", bg: "#ffedd5" },
  revocada:  { label: "Revocado",  color: "#ef4444", bg: "#fee2e2" },
};

export function emailValido(email) {
  const e = String(email || "").trim();
  return e.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

async function invocar(body) {
  let data, error;
  try {
    ({ data, error } = await supabase.functions.invoke(FUNCION, { body }));
  } catch (e) {
    error = e;
  }
  if (error) throw await errorDeFuncion(error);
  return data;
}

export function invitarCliente({ proyectoId, email, nombre, contactoId }) {
  return invocar({
    action: "invitar",
    proyecto_id: proyectoId,
    email: String(email).trim().toLowerCase(),
    nombre: String(nombre).trim(),
    contacto_id: contactoId || null,
  });
}

export function reenviarInvitacion(accesoId) {
  return invocar({ action: "reenviar", acceso_id: accesoId });
}

export async function listarAccesos(proyectoId) {
  const { data, error } = await supabase.rpc("accesos_de_proyecto", { p_proyecto_id: proyectoId });
  if (error) throw errorDeRpc(error);
  return data || [];
}

export async function revocarAcceso(accesoId, motivo = "") {
  const { data, error } = await supabase.rpc("revocar_acceso_proyecto",
    { p_acceso_id: accesoId, p_motivo: motivo });
  if (error) throw errorDeRpc(error);
  if (!data?.success) throw new Error(data?.error || "No fue posible revocar el acceso.");
  return data;
}

// Nombre y contacto asociado. El correo no se edita aquí (ver corregirCorreoInvitacion).
export async function editarAcceso(accesoId, { nombre, contactoId }) {
  const { data, error } = await supabase.rpc("editar_acceso_proyecto",
    { p_acceso_id: accesoId, p_nombre: String(nombre || "").trim(), p_contacto_id: contactoId || null });
  if (error) throw errorDeRpc(error);
  if (!data?.success) throw new Error(data?.error || "No fue posible guardar los cambios.");
  return data;
}

// Corregir el correo de una invitación pendiente: el reemplazo es atómico en el
// servidor (revoca la original con su token y registra la nueva en una transacción;
// el correo se envía después). Si el correo falla, la nueva queda pendiente para
// “Reenviar” y la anterior no se reactiva.
export async function corregirCorreoInvitacion({ acceso, email, nombre, contactoId }) {
  try {
    await invocar({
      action: "reemplazar",
      acceso_id: acceso.id,
      email: String(email).trim().toLowerCase(),
      nombre: String(nombre).trim(),
      contacto_id: contactoId || null,
    });
    return { correoEnviado: true };
  } catch (e) {
    if (e.codigo === "correo_fallido") return { correoEnviado: false };
    throw e;
  }
}

export async function listarContactosActivos() {
  const { data, error } = await supabase.from("contactos")
    .select("id, razon_social, rut, email").eq("activo", true).order("razon_social");
  if (error) throw error;
  return data || [];
}

// ── Lado cliente (portal) ──────────────────────────────────────────────────
export async function aceptarInvitacion(token) {
  const { data, error } = await supabase.rpc("aceptar_invitacion", { p_token: token });
  if (error) throw error;
  return data;
}

export async function perfilPortal() {
  const { data, error } = await supabase.rpc("portal_mi_perfil");
  if (error) throw error;
  return data;
}

export function mensajeAceptacion(r) {
  switch (r?.resultado) {
    case "aceptada":
      return { tipo: "ok", texto: `Listo: ya tienes acceso al proyecto “${r.proyecto_nombre}”.` };
    case "ya_tiene_acceso":
      return { tipo: "ok", texto: `Ya tenías acceso al proyecto “${r.proyecto_nombre}”.` };
    case "vencida":
      return { tipo: "error", texto: "La invitación venció. Pide al equipo de TuAplicacion que te la reenvíe." };
    case "revocada":
      return { tipo: "error", texto: "Esta invitación fue anulada. Contacta al equipo de TuAplicacion." };
    case "correo_distinto":
      return { tipo: "error", texto: `Esta invitación fue enviada a ${r.correo_invitado}. Cierra sesión e ingresa con ese correo.` };
    case "correo_no_confirmado":
      return { tipo: "error", texto: "Debes confirmar tu correo con el enlace que te enviamos antes de aceptar la invitación." };
    default:
      return { tipo: "error", texto: "El enlace de invitación no es válido o ya fue utilizado." };
  }
}

// Token de invitación pendiente: sobrevive al login y a la creación de
// contraseña. Solo en la pestaña actual (sessionStorage).
// Formato del token de invitación (base64url de 32 bytes; la base exige 32–128). Solo se guarda si lo cumple:
// un valor ajeno puesto en la URL nunca llega a sessionStorage ni a la base.
export const FORMATO_TOKEN = /^[A-Za-z0-9_-]{32,128}$/;
export function guardarTokenPendiente(token) {
  try { if (typeof token === "string" && FORMATO_TOKEN.test(token)) sessionStorage.setItem(CLAVE_TOKEN, token); } catch { /* almacenamiento no disponible */ }
}
export function leerTokenPendiente() {
  try { return sessionStorage.getItem(CLAVE_TOKEN); } catch { return null; }
}
export function borrarTokenPendiente() {
  try { sessionStorage.removeItem(CLAVE_TOKEN); } catch { /* sin almacenamiento */ }
}
